import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPencil, faXmark } from '@fortawesome/free-solid-svg-icons';
import { updateLastActivity } from '../../Utils/LastActivity';
import { appBarHeight, navBarHeight } from '../../Constants';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import MenuItem from '@mui/material/MenuItem';
import type { SelectChangeEvent } from '@mui/material';
import { Button, Divider, FormControl, InputLabel, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { getUniqueId } from 'shouldve-been-js';
import { useTitle } from 'shouldve-been-react';
import { ErrorSnackbar, LoadDataBackdrop, ConfirmDialog } from 'shouldve-been-mui';
import BugReport from '../../Models/BugReportModel';
import FilledObservationLine from '../../Models/FilledObservationLineModel';
import type ObservationModel from '../../Models/ObservationModelModel';
import FilledObservation from '../../Models/FilledObservationModel';
import type ObservationChapter from '../../Models/ObservationChapterModel';
import type ObservationSubChapter from '../../Models/ObservationSubChapterModel';
import type ObservationRuleSet from '../../Models/ObservationRuleSetModel';
import EditObservationLineDialog from '../../Dialogs/ObservationDialogs/EditObservationLineDialog';
import ObservationCustomComponentType from '../../Enums/ObservationCustomComponentType';

type ObservationItemSubTabProps = {
    filledObservationId: string,
    observationId: string,
    sortIndex: number,
    closeSubTab: (successMessage: string | null) => void,
};

function ObservationItemSubTab(props: Readonly<ObservationItemSubTabProps>): JSX.Element {

    useTitle('K-Fleet Experience - Observation Item');

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);

    const currentTheme: Theme = useTheme();

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [errorSnackbarMessage, setErrorSnackbarMessage] = useState<string | null>(null);
    const [dataIsLoaded, setDataIsLoaded] = useState<boolean>(false);
    const [showEditObservationLineDialog, setShowEditObservationLineDialog] = useState<string | null>(null);
    const [showConfirmObservationLineDeletionDialog, setShowConfirmObservationLineDeletionDialog] = useState<string | null>(null);

    const [name, setName] = useState<string>('');
    const [createdDate, setCreatedDate] = useState<Date>(new Date(0));
    const [ruleSetId, setRuleSetId] = useState<string>('');
    const [observationLines, setObservationLines] = useState<FilledObservationLine[]>([]);
    const [observationModel, setObservationModel] = useState<ObservationModel | null>(null);
    const [folderId, setFolderId] = useState<string | null>(null);

    const [chapters, setChapters] = useState<ObservationChapter[]>([]);
    const [subChapters, setSubChapters] = useState<ObservationSubChapter[]>([]);
    const [ruleSets, setRuleSets] = useState<ObservationRuleSet[]>([]);

    const ruleSetMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => observationModel?.ruleSetIds.map((currentRuleSetId: string) => {

        const matchingRuleSet: ObservationRuleSet | undefined = ruleSets.find((currentRuleSet: ObservationRuleSet) => currentRuleSet.ruleSetId === currentRuleSetId);

        return (
            <MenuItem
                key={currentRuleSetId}
                value={currentRuleSetId}
            >
                {matchingRuleSet?.name ?? ''}
            </MenuItem>
        );
    }) ?? [], [observationModel?.ruleSetIds, ruleSets]);

    const tableRows: JSX.Element[] = useMemo<JSX.Element[]>(() => observationLines.map((observationLine: FilledObservationLine) => {

        return (
            <TableRow
                key={observationLine.observationLineId}
            >
                <TableCell>
                    {observationLine.subChapterId === '' ? 'N/A' : observationLine.subChapterId}
                    <br />
                    {observationLine.getFirstFieldSummary(observationModel, 20)}
                </TableCell>
                <TableCell>
                    <IconButton
                        onClick={(): void => setShowEditObservationLineDialog(observationLine.observationLineId)}
                    >
                        <FontAwesomeIcon
                            icon={faPencil}
                            color={currentTheme.palette.text.primary}    
                        />
                    </IconButton>
                </TableCell>
                <TableCell>
                    <IconButton
                        onClick={(): void => setShowConfirmObservationLineDeletionDialog(observationLine.observationLineId)}
                    >
                        <FontAwesomeIcon
                            icon={faXmark}
                            color={currentTheme.palette.error.main}    
                        />
                    </IconButton>
                </TableCell>
            </TableRow>
        );
    }), [currentTheme.palette.error.main, currentTheme.palette.text.primary, observationLines, observationModel]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => { //fetch data from indexedDB

        let mounted: boolean = true;
        
        async function fetchFromIndexedDB(): Promise<void> {

            const newFilledObservation: FilledObservation | null = await indexedDBContext.indexedDBService.getFilledObservation(props.filledObservationId);
            const newObservationModel: ObservationModel | null = await indexedDBContext.indexedDBService.getObservationModel(props.observationId);
            const newChapters: ObservationChapter[] = await indexedDBContext.indexedDBService.getAllObservationChapters();
            const newSubChapters: ObservationSubChapter[] = await indexedDBContext.indexedDBService.getAllObservationSubChapters();
            const newRuleSet: ObservationRuleSet[] = await indexedDBContext.indexedDBService.getAllObservationRuleSets();

            if (mounted) {
                if (newFilledObservation === null || newObservationModel === null) {
                    return;
                }

                if (newObservationModel.ruleSetIds.length === 1) {
                    setRuleSetId(newObservationModel.ruleSetIds[0] ?? '');
                } else {
                    setRuleSetId(newFilledObservation.ruleSetId);
                }

                setName(newFilledObservation.name);
                setCreatedDate(newFilledObservation.createdDate);
                setObservationLines(newFilledObservation.observationLines);
                setObservationModel(newObservationModel);
                setFolderId(newFilledObservation.folderId);
                setChapters(newChapters);
                setSubChapters(newSubChapters);
                setRuleSets(newRuleSet);

                setDataIsLoaded(true);
            }
        }

        void fetchFromIndexedDB();

        return () => {
            mounted = false;
        };
    }, [indexedDBContext.indexedDBService, props.filledObservationId, props.observationId]);

    async function handleRuleSetChange(newRuleSetId: string): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        if (folderId === null) { //should not happen
            await indexedDBContext.indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Observation Item', 'Required field folderId is null when trying to update observation rule set'));
            setErrorSnackbarMessage('An unexpected error occurred');
            return;
        }

        const newChangedDate: Date = new Date();

        const success: boolean = await indexedDBContext.indexedDBService.updateFilledObservation(new FilledObservation(props.filledObservationId, props.observationId, name, createdDate, newChangedDate, observationLines, folderId, props.sortIndex, newRuleSetId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to save observation');
            return;
        }
        
        setRuleSetId(newRuleSetId);
    }

    async function handleAddObservationLine(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        if (folderId === null) { //should not happen
            await indexedDBContext.indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Observation Item', 'Required field folderId is null when trying to add observation line'));
            setErrorSnackbarMessage('An unexpected error occurred');
            return;
        }

        if (observationModel === null) { //should not happen
            await indexedDBContext.indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Observation Item', 'Observation model is null when trying to add observation line'));
            return;
        }

        const newChangedDate: Date = new Date();

        const custom1InitialValue: string = observationModel.customComponents.length > 0 && observationModel.customComponents[0]?.type === ObservationCustomComponentType.COMBO ? observationModel.customComponents[0].defaultValue : '';
        const custom2InitialValue: string = observationModel.customComponents.length > 1 && observationModel.customComponents[1]?.type === ObservationCustomComponentType.COMBO ? observationModel.customComponents[1].defaultValue : '';
        const custom3InitialValue: string = observationModel.customComponents.length > 2 && observationModel.customComponents[2]?.type === ObservationCustomComponentType.COMBO ? observationModel.customComponents[2].defaultValue : '';
        const custom4InitialValue: string = observationModel.customComponents.length > 3 && observationModel.customComponents[3]?.type === ObservationCustomComponentType.COMBO ? observationModel.customComponents[3].defaultValue : '';

        const newObservationLine: FilledObservationLine = new FilledObservationLine(getUniqueId(), '', 0, '', '', '', '', '', '', newChangedDate, custom1InitialValue, custom2InitialValue, custom3InitialValue, custom4InitialValue);

        const newObservationLines: FilledObservationLine[] = [...observationLines];
        newObservationLines.push(newObservationLine);

        const success: boolean = await indexedDBContext.indexedDBService.updateFilledObservation(new FilledObservation(props.filledObservationId, props.observationId, name, createdDate, newChangedDate, newObservationLines, folderId, props.sortIndex, ruleSetId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to add observation line');
            return;
        }

        setObservationLines(newObservationLines);
        setShowEditObservationLineDialog(newObservationLine.observationLineId);
    }

    async function handleDeleteObservationLine(): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        if (folderId === null) { //should not happen
            await indexedDBContext.indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Observation Item', 'Required field folderId is null when trying to delete observation line'));
            setErrorSnackbarMessage('An unexpected error occurred');
            return;
        }

        const newChangedDate: Date = new Date();

        const newObservationLines: FilledObservationLine[] = observationLines.filter((observationLine: FilledObservationLine) => observationLine.observationLineId !== showConfirmObservationLineDeletionDialog);

        const success: boolean = await indexedDBContext.indexedDBService.updateFilledObservation(new FilledObservation(props.filledObservationId, props.observationId, name, createdDate, newChangedDate, newObservationLines, folderId, props.sortIndex, ruleSetId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to delete observation line');
            return;
        }

        setObservationLines(newObservationLines);
    }

    async function handleCloseEditObservationLineDialog(newObservationLine: FilledObservationLine): Promise<void> {

        updateLastActivity(userContext, localStorageContext);

        if (folderId === null) { //should not happen
            await indexedDBContext.indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Observation Item', 'Required field folderId is null when trying to delete observation line'));
            setErrorSnackbarMessage('An unexpected error occurred');
            return;
        }

        const newChangedDate: Date = new Date();

        const newObservationLines: FilledObservationLine[] = [...observationLines];

        const editedObservationLineIndex: number = newObservationLines.findIndex((observationLine: FilledObservationLine) => observationLine.observationLineId === newObservationLine.observationLineId);
    
        if (editedObservationLineIndex === -1) { //should not happen
            await indexedDBContext.indexedDBService.insertBugReport(new BugReport(getUniqueId(), new Date(), 'Observation Item', 'Edited observation line was no longer found'));
            setErrorSnackbarMessage('Edited observation line was no longer found');
            return;
        }

        newObservationLines[editedObservationLineIndex] = newObservationLine;

        const success: boolean = await indexedDBContext.indexedDBService.updateFilledObservation(new FilledObservation(props.filledObservationId, props.observationId, name, createdDate, newChangedDate, newObservationLines, folderId, props.sortIndex, ruleSetId)); 
        
        if (!success) {
            setErrorSnackbarMessage('Failed to save observation line');
            return;
        }

        setObservationLines(newObservationLines);
        setShowEditObservationLineDialog(null);
    }

    return (
        <>
            <AppBar 
                position='sticky'
                elevation={0}
                style={{
                    background: currentTheme.palette.background.paper,
                    height: appBarHeight,
                }}
            >
                <Toolbar style={{
                    justifyContent: 'left',
                }}>
                    <IconButton 
                        edge='end' 
                        color='inherit' 
                        onClick={(): void => props.closeSubTab(null)}
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            color={currentTheme.palette.text.primary}    
                        />
                    </IconButton>
                    <Typography
                        noWrap
                        style={{
                            marginLeft: '30px',
                            color: currentTheme.palette.text.primary,
                            
                        }}
                    >
                        {'Observation "' + name + '"'}
                    </Typography>
                </Toolbar>
            </AppBar>
            <div style={{
                textAlign: 'center',
                padding: '25px 5px 5px 5px',
                height: windowSize[1] - navBarHeight - appBarHeight,
                overflow: 'auto',
                position: 'relative',
            }}>
                <FormControl 
                    size='small'
                    style={{
                        width: Math.min(windowSize[0] * 0.8, 500),
                        textAlign: 'left',
                    }}
                >
                    <InputLabel>Rule Set</InputLabel>
                    <Select
                        value={ruleSetId}
                        onChange={async (e: SelectChangeEvent): Promise<void> => handleRuleSetChange((e.target as HTMLInputElement).value)}
                        size='small'
                        variant='outlined'
                        label='Rule Set'
                        disabled={observationLines.length > 0 || observationModel === null || !observationModel.ruleSetsActive || observationModel.ruleSetIds.length < 2}
                    >
                        {ruleSetMenuItems}
                    </Select>
                </FormControl>
                <br />
                <br />
                <Divider />
                <br />
                <span style={{
                    color: currentTheme.palette.text.primary,
                    fontWeight: 'bold',
                }}>
                    Observation Lines:
                </span>
                <br />
                <br />
                <Button
                    onClick={handleAddObservationLine}
                    color='primary'
                    variant='contained'
                    disabled={ruleSetId === '' && observationModel !== null && observationModel.ruleSetsActive}
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Add Line
                </Button>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    Subchapter/Content
                                </TableCell>
                                <TableCell>
                                </TableCell>
                                <TableCell>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableRows}
                        </TableBody>
                    </Table>
                </TableContainer>
                <br />
                <br />

                <ErrorSnackbar 
                    message={errorSnackbarMessage}
                    setMessage={setErrorSnackbarMessage}
                />

                <LoadDataBackdrop
                    dataIsLoaded={dataIsLoaded}
                    zIndex={1000}
                />
            </div>

            {showEditObservationLineDialog !== null && observationModel !== null &&
                <EditObservationLineDialog
                    controller={showEditObservationLineDialog !== null}
                    onClose={handleCloseEditObservationLineDialog}
                    observationLineId={showEditObservationLineDialog}
                    chapters={chapters}
                    subChapters={subChapters}
                    observationModel={observationModel}
                    observationLines={observationLines}
                    ruleSetId={ruleSetId}
                />
            }

            <ConfirmDialog 
                controller={showConfirmObservationLineDeletionDialog !== null}
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmObservationLineDeletionDialog(null);
                    if (confirm) {
                        await handleDeleteObservationLine();
                    }
                }}
                title='Delete observation line'
                text='Are you sure you want to delete this observation line?'
                noText='No'
                yesText='Yes'
                loadingConfirm={true}
            />
        </>
    );
}

export default ObservationItemSubTab;